import { useQuery } from '@tanstack/react-query';
import { requestTyped } from '@xdocs/ws-protocol';
import type { AdminAnalyticsOverviewResult, AdminAnalyticsTimeseriesResult, AnalyticsMetric, ErrorAlert } from '@xdocs/schemas';
import { useWs } from '../../ws/WsProvider.js';

export interface UseAdminAnalyticsOverviewResult {
  overview: AdminAnalyticsOverviewResult | null;
  isLoading: boolean;
  isError: boolean;
  error: ErrorAlert | null;
  refetch: () => void;
}

/** `admin.analytics.overview` (F71) — the cached PG read-model headline
 * numbers + generation-by-worker breakdown + top public assets, shared by
 * `/admin/overview` and `/admin/analytics`. */
export function useAdminAnalyticsOverview(): UseAdminAnalyticsOverviewResult {
  const { wsClient, connectionState } = useWs();
  const enabled = connectionState === 'connected';

  const result = useQuery<AdminAnalyticsOverviewResult, ErrorAlert>({
    queryKey: ['admin', 'analytics', 'overview'],
    queryFn: () => requestTyped(wsClient, 'admin.analytics.overview', undefined),
    enabled,
    // Cached read-model — a fresh mount refetching every few seconds would
    // just re-serve the same pg_cron-refreshed row; keep the previous
    // result on screen for a short window instead of re-flashing loading.
    staleTime: 30_000,
  });

  return {
    overview: result.data ?? null,
    isLoading: result.isLoading,
    isError: result.isError,
    error: result.error ?? null,
    refetch: () => void result.refetch(),
  };
}

export interface UseAdminAnalyticsTimeseriesResult {
  points: AdminAnalyticsTimeseriesResult['points'];
  isLoading: boolean;
  isError: boolean;
  error: ErrorAlert | null;
  refetch: () => void;
}

/** `admin.analytics.timeseries` (F71) — one metric, bucketed over a bounded range. */
export function useAdminAnalyticsTimeseries(metric: AnalyticsMetric, from: Date, to: Date, granularity: 'day' | 'week' = 'day'): UseAdminAnalyticsTimeseriesResult {
  const { wsClient, connectionState } = useWs();
  const enabled = connectionState === 'connected';

  const result = useQuery<AdminAnalyticsTimeseriesResult, ErrorAlert>({
    queryKey: ['admin', 'analytics', 'timeseries', metric, from.toISOString(), to.toISOString(), granularity],
    queryFn: () => requestTyped(wsClient, 'admin.analytics.timeseries', { metric, from, to, granularity }),
    enabled,
    staleTime: 30_000,
  });

  return {
    points: result.data?.points ?? [],
    isLoading: result.isLoading,
    isError: result.isError,
    error: result.error ?? null,
    refetch: () => void result.refetch(),
  };
}
